import { useEffect, useRef } from 'react'
import { useCustomer, useStaff } from './useSession'
import { Platform } from 'react-native'
import AppConfig from 'config'
import Constants from 'expo-constants'

import {
  checkForUpdateAsync,
  reloadAsync,
  useUpdates,
  fetchUpdateAsync,
} from 'expo-updates'
import useAppActivity from './activity/useAppActivity'
import { useUpdateModalState } from './useUpdateModal'

/**
 * Automatic updates
 *
 * Runs in the background and checks for updates
 * only if there is no customer or staff currently logged in
 * and only on native platforms
 *
 * Disabled in Expo debug mode - the update methods will throw in this case
 */
const useAutoUpdate = () => {
  const customer = useCustomer()
  const staff = useStaff()
  const updates = useUpdates()
  const isUpdatePending = useRef(updates.isUpdatePending)
  const isUserActive = useAppActivity((s) => s.active)

  const shouldRestart = useRef(!isUserActive)

  useEffect(() => {
    isUpdatePending.current = updates.isUpdatePending
  }, [updates.isUpdatePending])

  useEffect(() => {
    shouldRestart.current = !isUserActive && !staff && !customer // Already pending
  }, [isUserActive, customer, staff])

  useEffect(() => {
    if (Platform.OS === 'web' || Constants.debugMode) {
      return
    }

    const handleUpdates = async () => {
      // Update already pending - reload the app
      try {
        if (isUpdatePending.current && shouldRestart.current) {
          reloadAsync()
          return
        }
        // Handle no auto restart
        if (isUpdatePending.current) {
          return
        }
        // Check for updates
        const res = await checkForUpdateAsync()
        if (!res.isAvailable) {
          return
        }
        const update = await fetchUpdateAsync()

        if (!update) {
          return
        }
        isUpdatePending.current = true
        if (shouldRestart.current) {
          reloadAsync()
          return
        }
      } catch (e) {
        console.error(e)
      }
    }

    // Run once before interval to check for updates on logouts etc.
    handleUpdates()
    const interval = setInterval(
      handleUpdates,
      AppConfig.remoteConfig.refetchInterval
    )

    return () => clearInterval(interval)
  }, [customer, staff])
}

export default useAutoUpdate
